import { useNavigate } from "react-router-dom";
import {
  BarChart3,
  Bell,
  Cable,
  CreditCard,
  Key,
  LogOut,
  Settings,
  User,
  UserPen,
} from "lucide-react";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { useAuth, authedFetch, useTenant, hasUserAuth } from "@/lib/auth";
import { resetUser } from "@/lib/posthog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { trpc } from "@/lib/trpc";
import { devLogout } from "@/lib/dev-auth";
import { DevUserMenuItems } from "./dev-user-menu";
import { themeLabels, useThemeCycle } from "./theme-toggle";

function ThemeToggleMenuItem() {
  const { theme, setTheme, next, Icon } = useThemeCycle();

  return (
    <DropdownMenuItem
      onSelect={(e) => {
        e.preventDefault();
        setTheme(next);
      }}
    >
      <Icon className="mr-2 h-3.5 w-3.5" />
      {themeLabels[theme]}
    </DropdownMenuItem>
  );
}

const planLabels: Record<string, string> = {
  free: "Free plan",
  pro: "Pro plan",
  team: "Team plan",
  enterprise: "Enterprise",
};

export function UserMenu({ onNavigate }: { onNavigate?: () => void }) {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const tenantId = useTenant();
  const { data: tenant } = trpc.tenant.getById.useQuery(
    { id: tenantId },
    { enabled: !!tenantId },
  );

  if (!auth || !hasUserAuth(auth)) return null;

  const displayLabel = auth.displayName || auth.email || "Account";
  const initialsSource = auth.displayName || auth.email?.split("@")[0] || "U";
  const initials = initialsSource
    .split(/[\s._-]/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? "")
    .join("");

  const isDev = auth.authMode === "dev";

  async function handleSignOut() {
    resetUser();
    if (isDev) {
      onNavigate?.();
      await devLogout();
      return;
    }
    const res = await authedFetch("/api/auth/logout", { method: "POST" });
    onNavigate?.();
    const data = await res.json().catch(() => null);
    if (data?.redirectUrl) {
      window.location.href = data.redirectUrl;
    } else {
      window.location.href = "/login";
    }
  }

  return (
    <div className="px-5 pb-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="group flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-sidebar-accent/60"
            aria-label="User menu"
          >
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sidebar-muted-foreground/15 text-xs font-bold text-sidebar-muted-foreground group-hover:text-sidebar-foreground">
              {initials[0] ?? "U"}
            </span>
            <div className="flex-1 min-w-0 leading-tight">
              <span className="flex items-center gap-1.5 text-sm text-sidebar-foreground">
                <span className="truncate">{displayLabel}</span>
                {isDev && (
                  <SimpleTooltip content="Local dev environment (AUTH_MODE=dev)">
                    <Badge
                      variant="outline"
                      tabIndex={0}
                      className="border-sidebar-border px-1.5 py-0 text-xs text-sidebar-foreground cursor-help"
                    >
                      DEV
                    </Badge>
                  </SimpleTooltip>
                )}
              </span>
              {tenant?.plan && (
                <span className="block truncate text-xs text-sidebar-muted-foreground">
                  {planLabels[tenant.plan] ?? tenant.plan}
                </span>
              )}
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="w-[var(--radix-dropdown-menu-trigger-width)]"
        >
          {auth.displayName && (
            <>
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                {auth.email}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
            Account
          </DropdownMenuLabel>
          <DropdownMenuItem
            onClick={() => {
              navigate("/account");
              onNavigate?.();
            }}
          >
            <User className="mr-2 h-3.5 w-3.5" />
            Account
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              navigate("/account#section-notifications");
              onNavigate?.();
            }}
          >
            <Bell className="mr-2 h-3.5 w-3.5" />
            Notifications
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              navigate("/account#section-billing");
              onNavigate?.();
            }}
          >
            <CreditCard className="mr-2 h-3.5 w-3.5" />
            Plan & billing
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              navigate("/account#section-profile");
              onNavigate?.();
            }}
          >
            <UserPen className="mr-2 h-3.5 w-3.5" />
            Profile
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
            Workspace
          </DropdownMenuLabel>
          <DropdownMenuItem
            onClick={() => {
              navigate("/settings");
              onNavigate?.();
            }}
          >
            <Settings className="mr-2 h-3.5 w-3.5" />
            Workspace settings
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              navigate("/settings?tab=access-tokens");
              onNavigate?.();
            }}
          >
            <Key className="mr-2 h-3.5 w-3.5" />
            Access tokens
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              navigate("/settings?tab=usage");
              onNavigate?.();
            }}
          >
            <BarChart3 className="mr-2 h-3.5 w-3.5" />
            Usage
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              navigate("/settings?tab=integrations");
              onNavigate?.();
            }}
          >
            <Cable className="mr-2 h-3.5 w-3.5" />
            Integrations
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
            Appearance
          </DropdownMenuLabel>
          <ThemeToggleMenuItem />
          {isDev && <DevUserMenuItems currentUserId={auth.userId} />}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleSignOut}>
            <LogOut className="mr-2 h-3.5 w-3.5" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
